/**
 * Full-text search over the bundled documentation (FR-HELP-05).
 *
 * Split from `help-content.ts` so the doc MODEL (parse, persona scope, ToC) and
 * the doc SEARCH stay separately readable. Everything runs in the browser over
 * the already-parsed pages — no request, no index build at startup.
 */
import { docsForPersona, type DocPage, type DocSearchHit, type DocSection } from './help-content';
import type { Persona } from './personas';

const EXCERPT_RADIUS = 80;

function excerptAround(body: string, index: number, length: number): string {
  const start = Math.max(0, index - EXCERPT_RADIUS);
  const end = Math.min(body.length, index + length + EXCERPT_RADIUS);
  const text = body.slice(start, end).replace(/\s+/g, ' ').trim();
  return `${start > 0 ? '…' : ''}${text}${end < body.length ? '…' : ''}`;
}

/** The heading a character offset falls under, or `null` above the first one. */
function sectionAt(page: DocPage, body: string, index: number): DocSection | null {
  let current: DocSection | null = null;
  let cursor = 0;
  for (const section of page.sections) {
    const found = body.indexOf(section.title, cursor);
    if (found === -1 || found > index) break;
    current = section;
    cursor = found + section.title.length;
  }
  return current;
}

/**
 * Full-text search across every bundled doc, restricted to what the persona may
 * see. Case-insensitive substring match with the matching section and an
 * excerpt, so the ToC can be filtered and the body highlighted.
 */
export function searchDocs(
  term: string,
  persona: Persona | null,
  isAllRoles = false
): DocSearchHit[] {
  const needle = term.trim().toLowerCase();
  if (needle === '') return [];

  const hits: DocSearchHit[] = [];
  for (const page of docsForPersona(persona, isAllRoles)) {
    const haystack = page.source.toLowerCase();
    const index = haystack.indexOf(needle);
    const isTitleMatch = page.title.toLowerCase().includes(needle);
    if (index === -1 && !isTitleMatch) continue;

    const section = index === -1 ? null : sectionAt(page, page.source, index);
    hits.push({
      slug: page.slug,
      title: page.title,
      sectionId: section?.id ?? null,
      sectionTitle: section?.title ?? null,
      excerpt: index === -1 ? page.title : excerptAround(page.source, index, needle.length),
    });
  }
  return hits;
}

/** Total match count for a doc — drives the "3 matches" badge in the ToC. */
export function countMatches(page: DocPage, term: string): number {
  const needle = term.trim().toLowerCase();
  if (needle === '') return 0;
  const haystack = page.source.toLowerCase();
  let count = 0;
  let index = haystack.indexOf(needle);
  while (index !== -1) {
    count += 1;
    index = haystack.indexOf(needle, index + needle.length);
  }
  return count;
}
